import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/newsletter")({
  
  head: () => ({ meta: [{ title: "Admin — Newsletter | Mine Class" }, { name: "description", content: "Panel de administración de Newsletter — acceso restringido a Mine Class." }, { name: "robots", content: "noindex, nofollow" }] }),
  component: NewsletterAdmin,
});

type Sub = {
  id: string;
  email: string;
  full_name: string | null;
  language: string | null;
  confirmed: boolean;
  unsubscribed_at: string | null;
  created_at: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
};

type Newsletter = {
  id: string;
  subject: string;
  body_html: string;
  status: string;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
};

function NewsletterAdmin() {
  const [tab, setTab] = useState<"subs" | "campaigns">("subs");
  const [subs, setSubs] = useState<Sub[]>([]);
  const [news, setNews] = useState<Newsletter[]>([]);
  const [editing, setEditing] = useState<Partial<Newsletter> | null>(null);

  async function load() {
    const [s, n] = await Promise.all([
      supabase.from("newsletter_subscribers").select("*").order("created_at", { ascending: false }).limit(500),
      supabase.from("newsletters").select("*").order("created_at", { ascending: false }),
    ]);
    setSubs((s.data ?? []) as Sub[]);
    setNews((n.data ?? []) as Newsletter[]);
  }
  useEffect(() => {
    load();
  }, []);

  async function saveCampaign() {
    if (!editing) return;
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const payload = {
      subject: editing.subject ?? "",
      body_html: editing.body_html ?? "",
      status: editing.status ?? "draft",
      scheduled_at: editing.scheduled_at ?? null,
      created_by: userId,
    };
    const q = editing.id
      ? supabase.from("newsletters").update(payload).eq("id", editing.id)
      : supabase.from("newsletters").insert(payload);
    const { error } = await q;
    if (error) {
      alert(error.message);
      return;
    }
    setEditing(null);
    load();
  }

  function exportCsv() {
    const rows = [
      ["email", "full_name", "language", "confirmed", "utm_source", "utm_medium", "utm_campaign", "created_at"],
      ...subs.map((s) => [
        s.email,
        s.full_name ?? "",
        s.language ?? "",
        String(s.confirmed),
        s.utm_source ?? "",
        s.utm_medium ?? "",
        s.utm_campaign ?? "",
        s.created_at,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (editing) {
    return (
      <div className="bg-white border border-[var(--color-outline-variant)] p-6 space-y-4 max-w-3xl">
        <h2 className="font-bold text-lg">{editing.id ? "Editar" : "Nueva"} campaña</h2>
        <input
          placeholder="Asunto"
          value={editing.subject ?? ""}
          onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
          className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
        />
        <textarea
          placeholder="HTML del correo"
          value={editing.body_html ?? ""}
          onChange={(e) => setEditing({ ...editing, body_html: e.target.value })}
          rows={16}
          className="w-full border border-[var(--color-outline-variant)] px-3 py-2 font-mono text-sm"
        />
        <select
          value={editing.status ?? "draft"}
          onChange={(e) => setEditing({ ...editing, status: e.target.value })}
          className="border border-[var(--color-outline-variant)] px-3 py-2"
        >
          <option value="draft">Borrador</option>
          <option value="scheduled">Programada</option>
          <option value="sent">Enviada</option>
        </select>
        <div className="flex gap-2">
          <button onClick={saveCampaign} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">
            Guardar
          </button>
          <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[var(--color-outline-variant)]">
            Cancelar
          </button>
        </div>
        <p className="text-xs text-[var(--color-on-surface-variant)]">
          El envío real se integra en una fase posterior (Lovable Emails o proveedor externo).
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b border-[var(--color-outline-variant)]">
        {(["subs", "campaigns"] as const).map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-semibold ${
              tab === k
                ? "border-b-2 border-[var(--color-brand-coral)] text-[var(--color-brand-purple)]"
                : "text-[var(--color-on-surface-variant)]"
            }`}
          >
            {k === "subs" ? `Suscriptores (${subs.length})` : `Campañas (${news.length})`}
          </button>
        ))}
      </div>

      {tab === "subs" && (
        <div className="space-y-3">
          <button onClick={exportCsv} className="px-3 py-2 border border-[var(--color-outline-variant)] text-sm">
            Exportar CSV
          </button>
          <div className="bg-white border border-[var(--color-outline-variant)] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface-container-low)] text-left">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Confirmado</th>
                  <th className="px-4 py-3">Origen (UTM)</th>
                  <th className="px-4 py-3">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-t border-[var(--color-outline-variant)]">
                    <td className="px-4 py-3 font-mono text-xs">{s.email}</td>
                    <td className="px-4 py-3">{s.full_name ?? "—"}</td>
                    <td className="px-4 py-3">
                      {s.confirmed ? (
                        <span className="text-green-700 text-xs font-bold">Sí</span>
                      ) : (
                        <span className="text-[var(--color-on-surface-variant)] text-xs">Pendiente</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {[s.utm_source, s.utm_medium, s.utm_campaign].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {subs.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-[var(--color-on-surface-variant)]">
                      Aún no hay suscriptores.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "campaigns" && (
        <div className="space-y-3">
          <button
            onClick={() => setEditing({ subject: "", body_html: "", status: "draft" })}
            className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold"
          >
            + Nueva campaña
          </button>
          <div className="bg-white border border-[var(--color-outline-variant)]">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface-container-low)] text-left">
                <tr>
                  <th className="px-4 py-3">Asunto</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Enviada</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {news.map((n) => (
                  <tr key={n.id} className="border-t border-[var(--color-outline-variant)]">
                    <td className="px-4 py-3 font-semibold">{n.subject}</td>
                    <td className="px-4 py-3 text-xs">{n.status}</td>
                    <td className="px-4 py-3 text-xs">{n.sent_at ? new Date(n.sent_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEditing(n)} className="text-xs underline">Editar</button>
                    </td>
                  </tr>
                ))}
                {news.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-[var(--color-on-surface-variant)]">
                      Sin campañas.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
